import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  BookOpen, PenLine, Smile, Heart, Frown, Zap, Brain,
  Plus, Trash2, Calendar, Tag, Save, ChevronLeft, ChevronRight
} from 'lucide-react';
import { getJournalEntries, getJournalEntryByDate, saveJournalEntry, deleteJournalEntry, getLocalDateString, parseLocalDateKey, shiftLocalDateKey } from '../lib/api';

const MOODS = [
  { id: 'grateful', icon: Heart, label_en: 'Grateful', label_hr: 'Zahvalan', color: 'text-pink-500 bg-pink-50' },
  { id: 'peaceful', icon: Smile, label_en: 'Peaceful', label_hr: 'Miran', color: 'text-green-500 bg-green-50' },
  { id: 'struggling', icon: Frown, label_en: 'Struggling', label_hr: 'U borbi', color: 'text-orange-500 bg-orange-50' },
  { id: 'motivated', icon: Zap, label_en: 'Motivated', label_hr: 'Motiviran', color: 'text-yellow-500 bg-yellow-50' },
  { id: 'reflective', icon: Brain, label_en: 'Reflective', label_hr: 'Zamišljen', color: 'text-purple-500 bg-purple-50' },
];

export default function Journal({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [currentEntry, setCurrentEntry] = useState({
    title: '',
    content: '',
    mood: null,
    tags: [],
    niyyah: '',
    shukr: ''
  });
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'history'

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    fetchEntryByDate(selectedDate);
  }, [selectedDate]);

  const fetchEntries = async () => {
    try {
      const data = await getJournalEntries();
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  const fetchEntryByDate = async (date) => {
    try {
      const data = await getJournalEntryByDate(date);
      if (data) {
        setCurrentEntry({
          title: data.title || '',
          content: data.content || '',
          mood: data.mood,
          tags: data.tags || [],
          niyyah: data.niyyah || '',
          shukr: data.shukr || ''
        });
      } else {
        setCurrentEntry({
          title: '',
          content: '',
          mood: null,
          tags: [],
          niyyah: '',
          shukr: ''
        });
      }
    } catch (error) {
      console.error('Error fetching entry:', error);
    }
  };

  const saveEntry = async () => {
    if (!currentEntry.title && !currentEntry.content) {
      toast.error(lang === 'hr' ? 'Dodaj naslov ili sadržaj' : 'Add a title or content');
      return;
    }

    setLoading(true);
    try {
      await saveJournalEntry(currentEntry, selectedDate);
      toast.success(lang === 'hr' ? 'Zapis spremljen!' : 'Entry saved!');
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(lang === 'hr' ? 'Greška pri spremanju' : 'Error saving');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (newTag && !currentEntry.tags.includes(newTag)) {
      setCurrentEntry(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setCurrentEntry(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const navigateDate = (direction) => {
    setSelectedDate(shiftLocalDateKey(selectedDate, direction));
  };

  const formatDate = (dateStr) => {
    const date = parseLocalDateKey(dateStr);
    return date.toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-6 py-8 md:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-[#292524]">
                {lang === 'hr' ? 'Bereket Dnevnik' : 'Barakah Journal'}
              </h1>
            </div>
            <p className="text-[#57534E]">
              {lang === 'hr' ? 'Bilježi svoja razmišljanja, nijet i zahvalnost' : 'Record your thoughts, intentions, and gratitude'}
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('edit')}
              data-testid="journal-edit-view-button"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === 'edit' ? 'bg-primary text-white' : 'bg-stone-100 text-[#57534E]'
              }`}
            >
              <PenLine className="w-4 h-4 inline mr-2" />
              {lang === 'hr' ? 'Piši' : 'Write'}
            </button>
            <button
              onClick={() => setViewMode('history')}
              data-testid="journal-history-view-button"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                viewMode === 'history' ? 'bg-primary text-white' : 'bg-stone-100 text-[#57534E]'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              {lang === 'hr' ? 'Povijest' : 'History'}
            </button>
          </div>
        </div>

        {viewMode === 'edit' ? (
          <div className="space-y-6">
            {/* Date Navigator */}
            <div className="flex items-center justify-center gap-4 bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-4">
              <button onClick={() => navigateDate(-1)} data-testid="journal-previous-date-button" className="p-2 hover:bg-stone-100 rounded-lg">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <p className="text-lg font-semibold text-[#292524]">{formatDate(selectedDate)}</p>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  data-testid="journal-date-input"
                  className="text-sm text-primary bg-transparent cursor-pointer"
                />
              </div>
              <button 
                onClick={() => navigateDate(1)} 
                data-testid="journal-next-date-button"
                className="p-2 hover:bg-stone-100 rounded-lg"
                disabled={selectedDate >= getLocalDateString()}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Niyyah (Intention) Section */}
            <div className="bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-[#292524] mb-3 flex items-center gap-2">
                🎯 {lang === 'hr' ? 'Današnji Nijet' : "Today's Intention (Niyyah)"}
              </h3>
              <textarea
                value={currentEntry.niyyah}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, niyyah: e.target.value }))}
                data-testid="journal-niyyah-textarea"
                placeholder={lang === 'hr' ? 'Što želiš postići danas? S kojom namjerom?' : 'What do you want to achieve today? With what intention?'}
                className="w-full px-4 py-3 bg-white/80 border border-stone-200 rounded-lg resize-none h-20 text-[#292524]"
              />
            </div>

            {/* Main Journal Entry */}
            <div className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-2xl p-6 shadow-card">
              <input
                type="text"
                value={currentEntry.title}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, title: e.target.value }))}
                data-testid="journal-title-input"
                placeholder={lang === 'hr' ? 'Naslov zapisa...' : 'Entry title...'}
                className="w-full text-2xl font-heading font-semibold text-[#292524] bg-transparent border-none outline-none mb-4 placeholder:text-stone-300"
              />
              
              <textarea
                value={currentEntry.content}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, content: e.target.value }))}
                data-testid="journal-content-textarea"
                placeholder={lang === 'hr' ? 'Piši svoja razmišljanja, učenja, izazove...' : 'Write your thoughts, lessons, challenges...'}
                className="w-full px-0 py-3 bg-transparent border-none resize-none h-48 text-[#292524] outline-none placeholder:text-stone-300"
              />

              {/* Mood Selector */}
              <div className="border-t border-stone-100 pt-4 mt-4">
                <p className="text-sm text-[#57534E] mb-3">{lang === 'hr' ? 'Kako se osjećaš?' : 'How are you feeling?'}</p>
                <div className="flex flex-wrap gap-2">
                  {MOODS.map(mood => {
                    const Icon = mood.icon;
                    return (
                      <button
                        key={mood.id}
                        onClick={() => setCurrentEntry(prev => ({ ...prev, mood: mood.id }))}
                        data-testid={`journal-mood-${mood.id}-button`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                          currentEntry.mood === mood.id 
                            ? `${mood.color} ring-2 ring-offset-2 ring-current` 
                            : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {lang === 'hr' ? mood.label_hr : mood.label_en}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              <div className="border-t border-stone-100 pt-4 mt-4">
                <p className="text-sm text-[#57534E] mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {lang === 'hr' ? 'Oznake' : 'Tags'}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {currentEntry.tags.map(tag => (
                    <span 
                      key={tag} 
                      className="inline-flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm"
                    >
                      #{tag}
                      <button onClick={() => removeTag(tag)} data-testid={`journal-remove-tag-${tag}`} className="hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    data-testid="journal-new-tag-input"
                    placeholder={lang === 'hr' ? 'Nova oznaka...' : 'New tag...'}
                    className="flex-1 px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm"
                  />
                  <button onClick={addTag} data-testid="journal-add-tag-button" className="px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Shukr (Gratitude) Section */}
            <div className="bg-gradient-to-r from-accent/5 to-pink-500/5 border border-accent/20 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-[#292524] mb-3 flex items-center gap-2">
                💝 {lang === 'hr' ? 'Šukr - Zahvalnost' : 'Shukr - Gratitude'}
              </h3>
              <textarea
                value={currentEntry.shukr}
                onChange={(e) => setCurrentEntry(prev => ({ ...prev, shukr: e.target.value }))}
                data-testid="journal-shukr-textarea"
                placeholder={lang === 'hr' ? 'Za što si zahvalan danas? Blagodati koje si primio...' : 'What are you grateful for today? Blessings you received...'}
                className="w-full px-4 py-3 bg-white/80 border border-stone-200 rounded-lg resize-none h-20 text-[#292524]"
              />
            </div>

            {/* Save Button */}
            <button
              onClick={saveEntry}
              disabled={loading}
              data-testid="journal-save-entry-button"
              className="w-full py-4 bg-primary text-white rounded-xl font-semibold text-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {loading ? (lang === 'hr' ? 'Spremanje...' : 'Saving...') : (lang === 'hr' ? 'Spremi Zapis' : 'Save Entry')}
            </button>
          </div>
        ) : (
          /* History View */
          <div className="space-y-4">
            {entries.length === 0 ? (
              <div className="text-center py-12 bg-white/80 rounded-xl">
                <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-[#57534E]">{lang === 'hr' ? 'Još nema zapisa' : 'No entries yet'}</p>
              </div>
            ) : (
              entries.map(entry => (
                <div 
                  key={entry.entry_id}
                  onClick={() => {
                    setSelectedDate(entry.date);
                    setViewMode('edit');
                  }}
                  data-testid={`journal-history-entry-${entry.entry_id}`}
                  className="bg-white/80 backdrop-blur-sm border border-stone-200/60 rounded-xl p-5 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-heading font-semibold text-[#292524]">{entry.title || (lang === 'hr' ? 'Bez naslova' : 'Untitled')}</h3>
                      <p className="text-sm text-[#A8A29E]">{formatDate(entry.date)}</p>
                    </div>
                    {entry.mood && (
                      <span className={`px-3 py-1 rounded-full text-sm ${MOODS.find(m => m.id === entry.mood)?.color || ''}`}>
                        {lang === 'hr' ? MOODS.find(m => m.id === entry.mood)?.label_hr : MOODS.find(m => m.id === entry.mood)?.label_en}
                      </span>
                    )}
                  </div>
                  <p className="text-[#57534E] line-clamp-2">{entry.content}</p>
                  {entry.tags?.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {entry.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs px-2 py-1 bg-stone-100 rounded">#{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
